/**
 * ARGOS Data Vault — Unified Chart Theme
 *
 * Single source of truth for all chart colors, gradients, and styling.
 * Inspired by Tableau 10 perceptually uniform palette, tuned for dark backgrounds.
 */

// ─── Perceptually Uniform Palettes ───────────────────────────────────
// Tableau 10-inspired, tuned for both dark and light backgrounds.

export const WELL_COLORS = [
  '#4e9ed6', // steel blue
  '#56c29c', // emerald
  '#e8654a', // coral
  '#f5b946', // amber
  '#9b7fd4', // lavender
  '#e88c4a', // tangerine
  '#5bbcd6', // cyan
  '#7bc86c', // sage
  '#d66ba0', // rose
  '#b0a8a0', // warm gray
] as const;

export const ANALYTE_COLORS = [
  '#e88c4a', // tangerine
  '#4e9ed6', // steel blue
  '#9b7fd4', // lavender
  '#e8654a', // coral
  '#56c29c', // emerald
  '#f5b946', // amber
  '#5bbcd6', // cyan
  '#d66ba0', // rose
  '#7bc86c', // sage
  '#b0a8a0', // warm gray
] as const;

// Semantic colors — use these for status indicators, not arbitrary chart series
export const STATUS_COLORS = {
  exceedance: '#ef4444',    // red-500
  warning: '#f59e0b',       // amber-500
  compliance: '#10b981',    // emerald-500
  info: '#3b82f6',          // blue-500
  neutral: '#6b7280',       // gray-500
  trending_up: '#ef4444',   // red (bad for contamination)
  trending_down: '#10b981', // green (good for contamination)
  trending_flat: '#6b7280', // gray
  non_detect: '#6b7280',    // gray
} as const;

// ─── Color Utilities ─────────────────────────────────────────────────

/** Get a well color by index (cycles through palette) */
export function getWellColor(index: number): string {
  return WELL_COLORS[index % WELL_COLORS.length];
}

/** Get an analyte color by index */
export function getAnalyteColor(index: number): string {
  return ANALYTE_COLORS[index % ANALYTE_COLORS.length];
}

/** Convert hex to rgba */
export function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ─── SVG Gradient Definitions ────────────────────────────────────────

/**
 * Creates a Recharts-compatible SVG linearGradient definition.
 * Usage in JSX:
 *   <defs>{createAreaGradient('grad-blue', '#4e9ed6')}</defs>
 *   <Area fill="url(#grad-blue)" />
 */
export function createAreaGradientDef(
  id: string,
  color: string,
  topOpacity = 0.35,
  bottomOpacity = 0.02,
) {
  return {
    id,
    color,
    topOpacity,
    bottomOpacity,
  };
}

/** Render SVG gradient defs for Recharts */
export function renderGradientDefs(gradients: ReturnType<typeof createAreaGradientDef>[]) {
  // This is a helper for JSX — call inside <defs>
  return gradients.map((g) => ({
    key: g.id,
    id: g.id,
    x1: '0', y1: '0', x2: '0', y2: '1',
    stops: [
      { offset: '0%', color: g.color, opacity: g.topOpacity },
      { offset: '100%', color: g.color, opacity: g.bottomOpacity },
    ],
  }));
}

// ─── Diverging Scales ────────────────────────────────────────────────

/** Exceedance ratio to color: green (0) → amber (1×MCL) → red (≥2×MCL) */
export function exceedanceColor(ratio: number): string {
  const clamped = Math.min(Math.max(ratio, 0), 3);
  if (clamped <= 0.5) {
    // Green zone
    return '#10b981';
  }
  if (clamped <= 1) {
    // Green → amber transition
    const t = (clamped - 0.5) / 0.5;
    return interpolateColor('#10b981', '#f59e0b', t);
  }
  if (clamped <= 2) {
    // Amber → red transition
    const t = (clamped - 1) / 1;
    return interpolateColor('#f59e0b', '#ef4444', t);
  }
  // Deep red
  return '#dc2626';
}

/** Simple linear color interpolation */
function interpolateColor(c1: string, c2: string, t: number): string {
  const r1 = parseInt(c1.slice(1, 3), 16);
  const g1 = parseInt(c1.slice(3, 5), 16);
  const b1 = parseInt(c1.slice(5, 7), 16);
  const r2 = parseInt(c2.slice(1, 3), 16);
  const g2 = parseInt(c2.slice(3, 5), 16);
  const b2 = parseInt(c2.slice(5, 7), 16);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/** Sequential scale: light to dark of a single hue. Returns array of N colors. */
export function sequentialScale(baseColor: string, steps: number): string[] {
  const colors: string[] = [];
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    colors.push(hexToRgba(baseColor, 0.15 + t * 0.85));
  }
  return colors;
}

// ─── Chart Styling Constants ─────────────────────────────────────────

export const CHART_STYLES = {
  /** Minimal grid: horizontal only, barely visible */
  grid: {
    vertical: false,
    strokeDasharray: '3 3',
    // Use CSS variable via inline style: var(--dv-chart-grid)
  },
  /** Axis tick styling */
  axisTick: {
    fontSize: 11,
    // fill via CSS variable: var(--dv-chart-axis)
  },
  /** Hide axis line for cleaner look */
  axisLine: false,
  /** Tooltip styling */
  tooltip: {
    borderRadius: 10,
    fontSize: 11,
    // Other styles from CSS variables
  },
  /** Chart margins */
  margin: { top: 12, right: 24, left: 12, bottom: 12 },
  /** Dot sizes */
  dot: { r: 2.5 },
  dotActive: { r: 5 },
  /** Line width */
  strokeWidth: 2,
  /** Bar settings */
  barRadius: [3, 3, 0, 0] as [number, number, number, number],
  barOpacity: 0.8,
  /** Animation */
  animationDuration: 400,
  animationEasing: 'ease-out' as const,
} as const;

// ─── Brush (Time-Range Zoom) Style ──────────────────────────────────

export const BRUSH_STYLES = {
  height: 30,
  stroke: 'var(--dv-border-subtle)',
  fill: 'rgba(10,10,10,0.8)',
  travellerWidth: 8,
  /** Only show brush when data has this many points or more */
  minDataPoints: 20,
  /** Extra bottom margin to accommodate brush */
  marginBottom: 44,
} as const;

// ─── MCL Reference Line Style ────────────────────────────────────────

export const MCL_STYLE = {
  stroke: '#ef4444',
  strokeWidth: 1.5,
  strokeDasharray: '6 4',
  label: {
    fill: '#ef4444',
    fontSize: 9,
    fontWeight: 600,
  },
} as const;

// ─── Chart Type Registry ─────────────────────────────────────────────
// Used by VizCategoryPanel for sidebar pills and ChartCanvas for rendering.

export interface ChartTypeDefinition {
  id: string;
  name: string;          // pill label
  category: string;
  component: string;     // lazy import path
  dataSource: 'analytical' | 'gauging' | 'risk' | 'cost' | 'plume' | 'compliance' | 'multi';
  description: string;
  isNew?: boolean;       // show "NEW" badge on pill
}

// Trimmed to the 18 favorites — the broader 70-chart library was deleted.
// Categories with zero favorites (gauges, environmental) are pruned from
// the sidebar entirely.
export const CHART_CATEGORIES = [
  { id: 'timeseries', label: 'Time Series & Trends', icon: 'TrendingUp' },
  { id: 'distribution', label: 'Distribution & Statistical', icon: 'BarChart3' },
  { id: 'comparison', label: 'Comparison & Ranking', icon: 'ArrowUpDown' },
  { id: 'composition', label: 'Composition', icon: 'PieChart' },
  { id: 'spatial', label: 'Spatial & Maps', icon: 'Map' },
  { id: 'matrix', label: 'Matrix & Grid', icon: 'Grid3x3' },
  { id: 'relationship', label: 'Relationships & Flow', icon: 'GitBranch' },
  { id: 'interactive', label: 'Interactive', icon: 'MousePointer2' },
] as const;

export const CHART_TYPES: ChartTypeDefinition[] = [
  // ── Time Series & Trends ──
  { id: 'dual-axis', name: 'Dual Axis', category: 'timeseries', component: 'DualAxisChart', dataSource: 'multi', description: 'Water elevation line + concentration bar overlay' },
  { id: 'regression', name: 'Regression', category: 'timeseries', component: 'TrendRegressionChart', dataSource: 'gauging', description: 'Scatter + 12-month projection line' },
  { id: 'fan', name: 'Fan', category: 'timeseries', component: 'FanChart', dataSource: 'analytical', description: 'Confidence bands (P10/P50/P90 envelopes)', isNew: true },
  { id: 'moving-average', name: 'Moving Avg', category: 'timeseries', component: 'MovingAverageChart', dataSource: 'analytical', description: 'Raw data + 3/6/12-month rolling average', isNew: true },
  { id: 'sparkline-grid', name: 'Sparkline Grid', category: 'timeseries', component: 'SparklineGrid', dataSource: 'analytical', description: 'Grid of labeled sparklines, 1 per well', isNew: true },

  // ── Distribution & Statistical ──
  { id: 'box-plot', name: 'Box Plot', category: 'distribution', component: 'ConcentrationDistributionChart', dataSource: 'analytical', description: 'Q1/median/Q3/whiskers per well' },
  { id: 'histogram', name: 'Histogram', category: 'distribution', component: 'HistogramChart', dataSource: 'analytical', description: 'Frequency distribution with MCL reference', isNew: true },
  { id: 'qq-plot', name: 'Q-Q Plot', category: 'distribution', component: 'QQPlot', dataSource: 'analytical', description: 'Normal/lognormal distribution check', isNew: true },

  // ── Comparison & Ranking ──
  { id: 'grouped-bar', name: 'Grouped Bar', category: 'comparison', component: 'ConcentrationBarChart', dataSource: 'analytical', description: 'Latest concentration by well, grouped by analyte' },

  // ── Composition ──
  { id: 'stacked-area', name: 'Stacked Area', category: 'composition', component: 'StackedAreaChart', dataSource: 'analytical', description: '100% stacked area over time', isNew: true },

  // ── Spatial & Maps ──
  { id: 'concentration-map', name: 'Heat Map', category: 'spatial', component: 'ConcentrationHeatmapMap', dataSource: 'multi', description: 'Interpolated concentration surface', isNew: true },
  { id: 'cross-section', name: 'Cross-Section', category: 'spatial', component: 'CrossSectionChart', dataSource: 'multi', description: 'Vertical well cross-section', isNew: true },

  // ── Matrix & Grid ──
  { id: 'exceedance', name: 'Exceedance', category: 'matrix', component: 'ExceedanceHeatmap', dataSource: 'analytical', description: 'Wells × dates, green→red gradient' },
  { id: 'pivot', name: 'Pivot', category: 'matrix', component: 'PivotHeatmap', dataSource: 'analytical', description: 'Configurable pivot table heatmap', isNew: true },

  // ── Relationships & Flow ──
  { id: 'scatter', name: 'Scatter', category: 'relationship', component: 'ConcentrationScatterChart', dataSource: 'analytical', description: 'Scatter with regression trend lines' },
  { id: 'sankey', name: 'Sankey', category: 'relationship', component: 'SankeyDiagram', dataSource: 'risk', description: 'Source → pathway → receptor flow', isNew: true },

  // ── Interactive ──
  { id: 'small-multiples', name: 'Small Multiples', category: 'interactive', component: 'SmallMultiplesGrid', dataSource: 'analytical', description: 'Mini chart array, 1 per well', isNew: true },
  { id: 'data-explorer', name: 'Data Explorer', category: 'interactive', component: 'DataExplorer', dataSource: 'multi', description: 'Build-your-own chart', isNew: true },
];

// ─── Helpers ─────────────────────────────────────────────────────────

/** Get all chart types for a given category */
export function getChartsByCategory(categoryId: string): ChartTypeDefinition[] {
  return CHART_TYPES.filter((c) => c.category === categoryId);
}

/** Get a chart type definition by ID */
export function getChartType(id: string): ChartTypeDefinition | undefined {
  return CHART_TYPES.find((c) => c.id === id);
}

/** Get chart types compatible with a given data source */
export function getChartsForDataSource(dataSource: string): ChartTypeDefinition[] {
  return CHART_TYPES.filter((c) => c.dataSource === dataSource || c.dataSource === 'multi');
}
